import { useCallback } from "react";

import type { GeoLocationInput, Maybe } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

type Props = {
	geoLocation:
		| ForAutoGenerator<GeoLocationInput | null | undefined, "">
		| ForAutoGenerator<Partial<GeoLocationInput> | null | undefined, "">;

	onSetViewModel: (
		values: {
			key: "geoLocation";
			value: ForAutoGenerator<Maybe<GeoLocationInput>, "">;
		}[],
	) => void;
};

export const useGeoLocation = ({ geoLocation, onSetViewModel }: Props) => {
	const onSetLatitude = useCallback(
		(number: number | undefined) => {
			if (number || geoLocation?.lon?.value) {
				/**
				 * どちらかが入力されている場合は、緯度の値を更新する
				 */
				const newValue = {
					lon: {
						isEdited: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
						value: geoLocation?.lon?.value!,
					},
					lat: {
						isEdited: true,
						value: number!,
					},
				} satisfies ForAutoGenerator<
					{
						lat?: number | null | undefined;
						lon?: number | null | undefined;
					},
					""
				>;

				onSetViewModel([
					{
						key: "geoLocation",
						value: newValue,
					},
				]);
			} else {
				onSetViewModel([
					{
						key: "geoLocation",
						value: null,
					},
				]);
			}
		},
		[geoLocation, onSetViewModel],
	);

	const onSetLongitude = useCallback(
		(number: number | undefined) => {
			if (number || geoLocation?.lat?.value) {
				/**
				 * どちらかが入力されている場合は、経度の値を更新する
				 */
				const newValue = {
					lon: {
						isEdited: true,
						value: number!,
					},
					lat: {
						isEdited: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
						value: geoLocation?.lat?.value!,
					},
				} satisfies ForAutoGenerator<
					{
						lat?: number | null | undefined;
						lon?: number | null | undefined;
					},
					""
				>;

				onSetViewModel([
					{
						key: "geoLocation",
						value: newValue,
					},
				]);
			} else {
				onSetViewModel([
					{
						key: "geoLocation",
						value: null,
					},
				]);
			}
		},
		[geoLocation, onSetViewModel],
	);

	const isEdited = geoLocation ? (geoLocation?.lat?.isEdited || geoLocation?.lon?.isEdited) ?? false : false;

	const hasError = Boolean(geoLocation?.lat?.error || geoLocation?.lon?.error);

	return {
		isEdited,
		hasError,

		geoLocation,
		onSetLatitude,
		onSetLongitude,
	};
};
